import React, { useMemo } from "react";
import { H5, Divider, Box, Flex, Badge } from "orcs-design-system";
import { get } from "lodash";
import { useAuth0 } from "src/contexts/auth0Provider";
import PATHS, { url } from "src/consts/urlPaths";
import {
  isInternalSupportUser,
  ADMIN_ROLE,
  ALLOWED_ROLES,
} from "src/services/auth";
import { isLocalEnvironment } from "src/services/localSettings";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import useUserRole from "src/app/Restricted/useUserRole";
import { canPerform } from "src/components/Can";
import SettingsLink from "./components/SettingsLink";

const SettingsContent = () => {
  const { user } = useAuth0();
  const userRole = useUserRole();
  const showDevTools = isInternalSupportUser(user) || isLocalEnvironment();
  const isAdmin = userRole === ADMIN_ROLE;
  const workspace = useWorkspace();

  const { enableStrategyStructure, enableWorkflows, enableDemoODOptions } = get(
    workspace,
    "config.featureFlags",
    {}
  );

  const showAuth0UserReportLink = canPerform(
    user,
    [userRole],
    ALLOWED_ROLES.USER_REPORT
  );

  const SETTINGS_LINKS = useMemo(() => {
    return [
      {
        id: "admin-dashboard",
        to: `${url(PATHS.ADMIN)}/branding`,
        label: "Tenant settings",
        hidden: !isAdmin,
      },
      {
        id: "manage-workspaces",
        to: url(PATHS.WORKSPACES),
        label: "Manage workspaces",
      },
      {
        id: "user-report",
        to: url(PATHS.USER_REPORT),
        label: "User Access Report",
        hidden: !showAuth0UserReportLink,
      },
    ];
  }, [isAdmin, showAuth0UserReportLink]);

  const WORKSPACE_CONFIGURATION_LINKS = useMemo(() => {
    return [
      {
        id: "workspace-settings",
        to: url(PATHS.WORKSPACE_SETTINGS, { tabType: "details" }),
        label: "Workspace settings",
        isActive: (path) => path.includes("workspace-settings"),
        hidden: false,
      },
      {
        id: "workspace-configuration-grouptypes",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "org-structure" }),
        label: "Org structure",
      },
      {
        id: "workspace-configuration-strategy-types",
        to: url(PATHS.WORKSPACE_CONFIGURATION, {
          tabType: "strategy-structure",
        }),
        label: "Strategy structure",
        hidden: !enableStrategyStructure,
      },
      {
        id: "workspace-configuration-tags",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "tags" }),
        label: "Manage tags",
      },
      {
        id: "manage-data-integrations-header-link",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "datasources" }),
        label: "Data integrations",
        hidden: !isAdmin,
      },
      {
        id: "workflows",
        to: url(PATHS.WORKFLOWS),
        label: "Workflows",
        hidden: !isAdmin || !enableWorkflows,
      },
      {
        id: "workspace-configuration-allocation",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "allocation" }),
        label: "Allocation config",
      },
      {
        id: "workspace-configuration-planner",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "planner" }),
        label: "Planner config",
      },
      {
        id: "workspace-configuration-forecast",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "forecast" }),
        label: "Forecast config",
      },
      {
        id: "workspace-configuration-strategy",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "strategy" }),
        label: "Strategy config",
      },
      {
        id: "workspace-configuration-groupaccess",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "group-access" }),
        label: "Power user access controls",
      },
      {
        id: "workspace-configuration-workspaceaccess",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "workspace-access" }),
        label: "Workspace access",
      },
      {
        id: "workspace-configuration-principles",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "principle-sets" }),
        label: "Principle sets",
      },
      {
        id: "workspace-configuration-notifications",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "notifications" }),
        label: "Workspace Communications",
        hidden: true,
      },
      {
        id: "workspace-configuration-features",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "features" }),
        label: "Workspace feature toggles",
        hidden: true,
      },
      {
        id: "workspace-configuration-other",
        to: url(PATHS.WORKSPACE_CONFIGURATION, { tabType: "details" }),
        label: "Workspace details",
        hidden: true,
      },
    ];
  }, [isAdmin, enableStrategyStructure, enableWorkflows]);

  return (
    <>
      <H5 fontWeight="bold">Settings</H5>
      <Box my="r">
        <Divider light />
      </Box>
      {SETTINGS_LINKS.filter((link) => !link.hidden).map((link) => (
        <SettingsLink key={link.id} {...link}>
          {link.label}
        </SettingsLink>
      ))}

      <Box my="r">
        <Divider light />
      </Box>
      <H5 fontSize="1.4rem" fontWeight="bold" color="subText" uppercase mb="r">
        Configure workspace
      </H5>

      {WORKSPACE_CONFIGURATION_LINKS.filter((link) => !link.hidden).map(
        (link) => (
          <SettingsLink key={link.id} {...link}>
            {link.label}
          </SettingsLink>
        )
      )}

      {(showDevTools || enableDemoODOptions) && (
        <>
          <Box my="r">
            <Divider light />
          </Box>
          {showDevTools && (
            <SettingsLink
              id="internal-dashboard"
              to={url(PATHS.INTERNAL_DASHBOARD_GENERAL)}
            >
              Internal devtool
            </SettingsLink>
          )}
          {/* Workspace feature flag enableDemoODOptions (same as Team Canvas). */}
          {enableDemoODOptions && (
            <SettingsLink
              id="org-design-settings"
              to={url(PATHS.ORG_DESIGN_SETTINGS)}
            >
              <Flex alignItems="center" justifyContent="space-between" gap="xs">
                Org Design settings
                <Badge variant="warning">BETA</Badge>
              </Flex>
            </SettingsLink>
          )}
        </>
      )}
    </>
  );
};

export default SettingsContent;
